import { useMemo } from 'react'
import type { FareClassWithReward } from 'constants/flight'
import useCharterDateRangesByMonth from './useCharterDateRangesByMonth'

interface UseCharterFlightDatesParams {
  checkInDate?: string
  /**
   * Number of nights for the selected bundle (e.g. 5 or 12)
   */
  nights?: number
  charterBundleRoute?: App.CharterBundleRoute
  rooms?: Array<App.Occupants>
  fareClass: FareClassWithReward
}

interface UseCharterFlightDatesResult {
  departDate?: string
  returnDate?: string
}

/**
 * Default rooms: 2 adults (similar to generateOfferPageRoom default)
 */
const DEFAULT_ROOMS: Array<App.Occupants> = [{ adults: 2 }]

/**
 * Custom hook to get charter flight dates (departDate/returnDate) from dateRangesByMonth
 * based on the selected checkInDate
 *
 * @param params - Configuration parameters
 * @returns Object containing:
 *   - departDate: Flight departure date from origin airport
 *   - returnDate: Flight arrival date back to origin airport
 */
function useCharterFlightDates(params: UseCharterFlightDatesParams): UseCharterFlightDatesResult {
  const { checkInDate, nights, charterBundleRoute, rooms, fareClass } = params

  // Use default rooms (2 adults) if rooms is not provided or empty
  const effectiveRooms = useMemo(() => {
    return rooms && rooms.length > 0 ? rooms : DEFAULT_ROOMS
  }, [rooms])

  // Get dateRangesByMonth to find departDate and returnDate for the selected checkInDate
  const { dateRangesByMonth } = useCharterDateRangesByMonth({
    charterBundleRoute,
    occupancies: effectiveRooms,
    fareClass,
    disabled: !charterBundleRoute || effectiveRooms.length === 0,
  })

  // Find departDate and returnDate from dateRangesByMonth based on checkInDate
  const { departDate, returnDate } = useMemo(() => {
    if (!checkInDate || !dateRangesByMonth) {
      return { departDate: undefined, returnDate: undefined }
    }

    const hasNights = typeof nights === 'number'
    let fallbackMatch: { departureDate: string; returnDate: string } | undefined

    // Single loop through all months: look for exact match, track fallback
    for (const ranges of Object.values(dateRangesByMonth)) {
      const exactMatch = ranges.find(range => {
        if (range.checkIn !== checkInDate) return false
        // If nights is provided, require exact nights match; otherwise any checkIn match is fine
        return !hasNights || range.nights === nights
      })

      if (exactMatch) {
        return {
          departDate: exactMatch.departureDate,
          returnDate: exactMatch.returnDate,
        }
      }

      // Store first checkIn-only match as fallback (only relevant when nights is provided)
      if (hasNights && !fallbackMatch) {
        fallbackMatch = ranges.find(range => range.checkIn === checkInDate)
      }
    }

    // Use fallback if available (when nights was provided but no exact match found)
    if (fallbackMatch) {
      return {
        departDate: fallbackMatch.departureDate,
        returnDate: fallbackMatch.returnDate,
      }
    }

    return { departDate: undefined, returnDate: undefined }
  }, [checkInDate, dateRangesByMonth, nights])

  return { departDate, returnDate }
}

export default useCharterFlightDates
